// Thin wrappers around the invitation + 2FA-enrollment endpoints used by the
// /invitations/:token acceptance flow. Kept separate from api.ts because the
// caller is an unauthenticated route that walks its own state machine; pulling
// these helpers into the shared auth module keeps the page focused on UI.

import { API_BASE_URL } from "./http";
import { AuthApiError } from "./api";
import type { AuthUser } from "./store";

async function parseError(res: Response): Promise<AuthApiError> {
  let code = `http_${res.status}`;
  let message: string | null = null;
  try {
    const body = (await res.json()) as {
      error?: unknown;
      message?: unknown;
    };
    if (typeof body.error === "string") code = body.error;
    if (typeof body.message === "string") message = body.message;
  } catch {
    // body wasn't JSON
  }
  return new AuthApiError(code, res.status, message);
}

export interface InvitationLookup {
  email: string;
  role: "admin" | "user";
}

export async function lookupInvitation(
  token: string,
): Promise<InvitationLookup> {
  const res = await fetch(
    `${API_BASE_URL}/invitations/${encodeURIComponent(token)}`,
    {
      method: "GET",
      headers: { accept: "application/json" },
    },
  );
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as InvitationLookup;
}

export interface AcceptInvitationResult {
  userId: string;
  setupToken: string;
}

export async function acceptInvitation(input: {
  token: string;
  name: string;
  password: string;
}): Promise<AcceptInvitationResult> {
  const res = await fetch(
    `${API_BASE_URL}/invitations/${encodeURIComponent(input.token)}/accept`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({ name: input.name, password: input.password }),
    },
  );
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as {
    user_id: string;
    next: string;
    setup_token: string;
  };
  return { userId: body.user_id, setupToken: body.setup_token };
}

export interface TotpSetupResult {
  otpauthUrl: string;
  secret: string;
}

export async function setup2fa(input: {
  mfaSession: string;
}): Promise<TotpSetupResult> {
  const res = await fetch(`${API_BASE_URL}/auth/2fa/setup`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify({ mfa_session: input.mfaSession }),
  });
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as { otpauth_url: string; secret: string };
  return { otpauthUrl: body.otpauth_url, secret: body.secret };
}

export interface Enable2faResult {
  accessToken: string;
  expiresIn: number;
  user: AuthUser;
}

export async function enable2fa(input: {
  mfaSession: string;
  code: string;
}): Promise<Enable2faResult> {
  const res = await fetch(`${API_BASE_URL}/auth/2fa/enable`, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify({ mfa_session: input.mfaSession, code: input.code }),
  });
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as {
    access_token: string;
    expires_in: number;
    user: AuthUser;
  };
  return {
    accessToken: body.access_token,
    expiresIn: body.expires_in,
    user: body.user,
  };
}
